<template>
  <BaseModalEditCreate
    :is-modified="isModified"
    max-width="fullMargin"
    :title="modalTitle"
    :hide-save-button="mode === 'view'"
    @dismiss-changes="closeModal"
    @save="onSave">
    <template #form>
      <div class="flex flex-col h-full w-full overflow-hidden">
        <!-- Tab Navigation -->
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9 mb-4">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="[
                selectedTab === tab.name
                  ? 'border-b-2 border-theme-500 text-theme-600'
                  : 'hover:text-gray-700',
              ]">
              {{ tab.label }}
            </li>
          </ul>
        </nav>

        <!-- Tab Content -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="selectedTab === 'mappingInfo'" class="p-6">
            <RecordMappingInfo
              v-if="parsedSchema"
              ref="recordMappingInfoRef"
              v-model:parsed-schema="parsedSchema"
              :mode="mode"
              :representative-code="representativeCode" />
          </div>
          <div v-else-if="selectedTab === 'mappings'" class="h-full">
            <AppResizableContainer class="h-full w-full" layout="vertical">
              <template #headerContent>
                <div v-if="parsedSchema" class="flex-1">
                  <RecordMappingsTable
                    v-model:mappings="parsedSchema.Mappings"
                    v-model:selected-record-mapping="selectedRecordMapping"
                    :mode="mode"
                    :entity-dropdown-items="entityDropdownItems" />
                </div>
              </template>
              <template #detailsContent>
                <div v-if="selectedRecordMapping && parsedSchema" class="h-full">
                  <RecordMappingFieldMappings
                    v-model:field-mappings="selectedRecordMapping.FieldMappings"
                    :mode="mode"
                    :mapping-type="parsedSchema.MappingInfo.MappingType"
                    :entity-fields="selectedEntityFields" />
                </div>
                <div v-else class="flex items-center justify-center h-full text-gray-500">
                  <p>{{ $t('representative.recordMapping.selectRecordMappingToViewFields') }}</p>
                </div>
              </template>
            </AppResizableContainer>
          </div>
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import {
  type RecordMapping,
  type RecordMappingSchema,
  type DataCollectionEntity,
  type FieldMapping,
  MappingType,
  validateRecordMappingSchema,
} from '@/types/RecordMapping';
import type { SelectDropdownItem } from '@/types/Base';
import RecordMappingsTable from './RecordMappingsTable.vue';
import RecordMappingFieldMappings from './RecordMappingFieldMappings.vue';
import RecordMappingInfo from './RecordMappingInfo.vue';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import { getDataCollectionEntities } from '@/services/api/dataCollection';
import { getDefaultCompanySite } from '@/services/api/companies';

const { t: $t } = useI18n();

// Tab navigation
const selectedTab = ref('mappingInfo');
const tabs = ref([
  {
    name: 'mappingInfo',
    label: $t('representative.recordMapping.mappingInfo'),
  },
  {
    name: 'mappings',
    label: $t('representative.recordMapping.mappings'),
  },
]);

const modalTitle = computed(() => {
  return props.mode === 'view'
    ? $t('representative.recordMapping.viewRecordMapping')
    : $t('representative.recordMapping.editRecordMapping');
});

const props = defineProps<{
  recordMapping: string;
  representativeId: string;
  representativeCode: string;
  mode: 'view' | 'edit';
}>();

const emit = defineEmits(['closeModal', 'save']);

const dataCollectionEntities = ref<DataCollectionEntity[]>([]);
const entityDropdownItems = computed(() => {
  if (!dataCollectionEntities.value) return [];
  return dataCollectionEntities.value.map(
    (entity) =>
      ({
        value: entity.tableName,
        name: entity.entityName,
      }) as SelectDropdownItem
  );
});

const selectedEntityFields = computed(() => {
  if (
    !selectedRecordMapping.value ||
    !selectedRecordMapping.value.TargetEntity ||
    !dataCollectionEntities.value
  )
    return [];
  return (
    dataCollectionEntities.value.find(
      (entity) => entity.tableName === selectedRecordMapping.value?.TargetEntity
    )?.properties || []
  );
});

const fetchDataCollectionEntities = async () => {
  try {
    const entities = await getDataCollectionEntities();
    dataCollectionEntities.value = entities;
  } catch (error) {
    console.error('Error fetching data collection entities:', error);
    dataCollectionEntities.value = [];
  }
};

onMounted(async () => {
  parsedSchema.value = await initializeParsedSchema();
  selectedRecordMapping.value = parsedSchema.value.Mappings[0];
  await fetchDataCollectionEntities();
  // Store the baseline JSON after all initialization completes
  originalJsonString.value = schemaToJson();
});

const createDefaultSchema = async (): Promise<RecordMappingSchema> => {
  const defaultCompanySite = await getDefaultCompanySite();
  return {
    MappingInfo: {
      MappingType: MappingType.CSV,
      SkipFirstLine: 0,
      Description: '',
      CreatedDate: new Date().toISOString(),
      RepresentativeID: props.representativeId,
      CompanyID: defaultCompanySite ? defaultCompanySite.companyID : '',
      CompanySiteID: defaultCompanySite ? defaultCompanySite.id : '',
      ConcatenationSettings: {
        DefaultSeparator: ',',
        AllowCustomSeparators: false,
        IncludeSeparatorInSource: false,
        Description: '',
      },
    },
    ImportSettings: {
      CSV: {
        Separator: '',
        QuoteCharacter: '',
        Encoding: '',
      },
      FixedLength: {
        RecordLength: null,
        PadCharacter: '',
      },
      JSON: {
        RootPath: '',
        ArrayPath: '',
        Encoding: '',
        ValidateSchema: false,
      },
      Database: {
        Query: '',
        ConnectionString: '',
        CommandTimeout: null,
      },
      RestApi: {},
      XLXS: {},
    },
    Mappings: [],
  };
};

const initializeParsedSchema = async (): Promise<RecordMappingSchema> => {
  try {
    if (!props.recordMapping || props.recordMapping.trim() === '')
      return await createDefaultSchema();

    const parsed = JSON.parse(props.recordMapping);
    if (validateRecordMappingSchema(parsed)) {
      const flattenedMappings = [...parsed.Mappings].reverse().map((mapping) => ({
        ...mapping,
        FieldMappings: flattenFieldMappings([...mapping.FieldMappings].reverse() || []),
      }));

      return {
        ...(await createDefaultSchema()),
        ...parsed,
        Mappings: flattenedMappings,
      };
    } else {
      console.warn('Invalid record mapping schema, using defaults');
      return await createDefaultSchema();
    }
  } catch (error) {
    console.error('Error parsing record mapping:', error);
    return await createDefaultSchema();
  }
};

const parsedSchema = ref<RecordMappingSchema | undefined>(undefined);
const selectedRecordMapping = ref<RecordMapping | undefined>(undefined);
const recordMappingInfoRef = ref();
const originalJsonString = ref<string>('');

const isModified = ref(false);
watch(
  () => parsedSchema.value,
  () => {
    if (!originalJsonString.value) return;
    isModified.value = originalJsonString.value !== schemaToJson();
  },
  { deep: true }
);

watch(
  () => selectedRecordMapping.value,
  () => {
    if (!originalJsonString.value) return;
    isModified.value = originalJsonString.value !== schemaToJson();
  },
  { deep: true }
);

function closeModal() {
  emit('closeModal');
}

function onSave() {
  // Prevent saving if date is invalid
  const dateValidation = recordMappingInfoRef.value?.getDateValidation();
  if (dateValidation && !dateValidation.isValid) {
    return;
  }

  const jsonString = schemaToJson();
  emit('save', jsonString);
}

// Data transformation functions for childFieldMappings
function flattenFieldMappings(mappings: FieldMapping[]): FieldMapping[] {
  const flattened: FieldMapping[] = [];

  for (const mapping of mappings) {
    flattened.push(mapping);

    if (mapping.ChildFieldMappings && mapping.ChildFieldMappings.length > 0) {
      for (const childMapping of mapping.ChildFieldMappings) {
        flattened.push({
          ...childMapping,
          TargetField: `${mapping.TargetField}.${childMapping.TargetField}`,
        });
      }
    }
  }

  return flattened;
}

function unflattenFieldMappings(flatMappings: FieldMapping[]): FieldMapping[] {
  const parentMappings: FieldMapping[] = [];
  const childMappings: { [parentTargetField: string]: FieldMapping[] } = {};
  // Process mappings in order to maintain insertion sequence
  for (const mapping of flatMappings) {
    if (mapping.TargetField.includes('.')) {
      const dotIndex = mapping.TargetField.indexOf('.');
      const parentTargetField = mapping.TargetField.substring(0, dotIndex);
      const childTargetField = mapping.TargetField.substring(dotIndex + 1);

      if (!childMappings[parentTargetField]) childMappings[parentTargetField] = [];

      // Push to end of array to maintain order
      childMappings[parentTargetField].push({
        ...mapping,
        TargetField: childTargetField,
      });
    } else {
      // Push to end of array to maintain order
      parentMappings.push({
        ...mapping,
        ChildFieldMappings: [],
      });
    }
  }

  // Attach child mappings to their parents at the end
  for (const parentMapping of parentMappings) {
    if (childMappings[parentMapping.TargetField]) {
      parentMapping.ChildFieldMappings = childMappings[parentMapping.TargetField];
    }
  }

  return parentMappings;
}

function uppercaseMappingFields(fieldMappings: FieldMapping[]): FieldMapping[] {
  return fieldMappings.map((fieldMapping) => {
    const processedMapping = { ...fieldMapping };

    if (processedMapping.MappingFields && Array.isArray(processedMapping.MappingFields)) {
      processedMapping.MappingFields = processedMapping.MappingFields.map((mappingField) => ({
        CSV: {
          ColumnIndex: mappingField.CSV.ColumnIndex,
          ColumnName:
            typeof mappingField.CSV.ColumnName === 'string'
              ? mappingField.CSV.ColumnName.toUpperCase()
              : mappingField.CSV.ColumnName,
        },
        FixedLength: mappingField.FixedLength,
        JSON: {
          JsonPath:
            typeof mappingField.JSON.JsonPath === 'string'
              ? mappingField.JSON.JsonPath.toUpperCase()
              : mappingField.JSON.JsonPath,
        },
        Database: {
          ColumnName:
            typeof mappingField.Database.ColumnName === 'string'
              ? mappingField.Database.ColumnName.toUpperCase()
              : mappingField.Database.ColumnName,
        },
      }));
    }

    if (processedMapping.ChildFieldMappings && Array.isArray(processedMapping.ChildFieldMappings)) {
      processedMapping.ChildFieldMappings = uppercaseMappingFields(
        processedMapping.ChildFieldMappings
      );
    }

    return processedMapping;
  });
}

const schemaToJson = (): string => {
  try {
    if (!parsedSchema.value) {
      return props.recordMapping;
    }
    // Convert flat fieldMappings back to nested structure before saving
    // Reverse arrays so newest items appear last in JSON (chronological order)
    const schemaToSave = {
      ...parsedSchema.value,
      Mappings: [...parsedSchema.value.Mappings].reverse().map((mapping) => ({
        ...mapping,
        FieldMappings: uppercaseMappingFields(
          unflattenFieldMappings([...mapping.FieldMappings].reverse() || [])
        ).map(
          ({ selectedTargetFieldItem: _, selectedDataType: __, ...cleanFieldMapping }) =>
            cleanFieldMapping
        ),
      })),
    };
    return JSON.stringify(schemaToSave);
  } catch (error) {
    console.error('Error stringifying schema:', error);
    return props.recordMapping;
  }
};
</script>
